import React from 'react';
import { Table } from '@tanstack/react-table';
import { Cross2Icon } from '@radix-ui/react-icons';
import { Input } from '../Input';
import { FacetedFilter } from './FacetedFilter';
import { FilterSettings } from './dataTableTypes';
import { Button } from '../Button';
import { DateRangeFilter } from './DateRangeFilter';
import { useClearStoredFilters } from './filterState';

// See ./index.tsx for props documentation
interface FilterBarProps<TData> {
  table: Table<TData>;
  tableName: string;
  enableSearch: boolean;
  filterString: string;
  filterPlaceholder: string;
  setFilterString: (value: string) => void;
  filters: FilterSettings[];
  dateRangeFilterColumnId?: string;
  // Reset the page index when the filter string changes.
  // This is because if the user is on page 2 and then filters
  // The table, the results may be less than the number of results per page,
  // and the user will see an empty table.
  resetPagination: () => void;
}

export function FilterBar<TData>({
  table,
  tableName,
  enableSearch,
  filterString,
  filterPlaceholder,
  setFilterString,
  filters,
  dateRangeFilterColumnId,
  resetPagination
}: FilterBarProps<TData>) {
  // isFiltered is true if there are any column filters (like the FacetedFilter or DateRangeFilters) or a global
  // filter string (i.e. search string) is set.
  const isFiltered = table.getState().columnFilters.length > 0 || filterString.length > 0;
  const clearStoredFilters = useClearStoredFilters();

  return (
    <div className="mb-2 flex flex-row justify-between rounded-lg border bg-muted p-2 text-on-widget">
      <div className="flex flex-col gap-y-2">
        <div className="flex flex-row flex-wrap gap-x-4 gap-y-4">
          {enableSearch ? (
            <Input
              placeholder={filterPlaceholder}
              value={filterString}
              onChange={(event) => {
                setFilterString(event?.target?.value);
              }}
              className="h-9 w-[150px] lg:w-[300px]"
            />
          ) : null}

          {dateRangeFilterColumnId ? (
            <DateRangeFilter column={table.getColumn(dateRangeFilterColumnId)} resetPagination={resetPagination} />
          ) : null}
        </div>
        <div className="flex flex-row flex-wrap gap-x-4 gap-y-4">
          {filters.map((filter, i) => {
            const column = table.getColumn(filter.columnId);

            if (!column) {
              return null;
            }

            return (
              <FacetedFilter
                key={`${filter.title}-${i}`}
                column={column}
                title={filter.title}
                options={filter.options}
                makeSticky={filter.makeSticky}
                tableName={tableName}
                resetPagination={resetPagination}
              />
            );
          })}
        </div>
      </div>
      <div>
        {isFiltered && (
          <Button
            variant="ghost"
            size="small"
            onClick={() => {
              setFilterString('');
              table.resetColumnFilters();
              clearStoredFilters(tableName);
              resetPagination();
            }}
            className="h-8 px-2 lg:px-3"
          >
            Reset Filters
            <Cross2Icon className="ml-2 h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
